import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'react-use';
import { Typography, Row, Pagination, Grid, Col, Select } from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import Icon from 'components/Shared/Common/Icon';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import styled from 'styled-components';

import useSearch from 'hooks/useSearch';

import FileList from 'components/FileList';
import MessageList from 'components/MessageList';
import CategoryTitle from 'components/Shared/Layout/CategoryTitle';
import { StyledPageLayout } from 'components/Shared/style';
import injectValidators from 'components/Shared/Forms/injectValidators';
import { StyledInput } from 'components/Shared/Forms/styles';
import { StyledTabs } from 'components/Shared/styles';

import { getUrl } from 'utils/url';
import documentTitleManager from 'services/documentTitleManager';

const { Text } = Typography;
const { useBreakpoint } = Grid;

const StyledText = styled(Text)`
    margin-top: 5px;
`;

const StyledLabel = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    .ant-badge {
        transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    }
    .icon {
        display: flex;
        align-items: center;
        margin-right: 5px;
        svg {
            width: 14px;
            height: 14px;
        }
    }
`;

function getCurrentBreakpoint(screens) {
    const breakpoints = ['xxl', 'xl', 'lg', 'md', 'sm', 'xs'];

    return breakpoints.find(bp => screens[bp]) || null;
}

// get page size options based on current breakpoint
function getPageSizeOptions(screens) {
    switch (getCurrentBreakpoint(screens)) {
        case 'xs':
        case 'sm':
            return [10, 20];
        case 'md':
            return [10, 20];
        case 'lg':
            return [12, 18];
        case 'xl':
            return [12, 20];
        case 'xxl':
            return [12, 18];
        default:
            return [12, 18];
    }
}

const FullWidthRow = styled(Row)`
    width: 100%;
`;

const FullWidthRowWithMargin = styled(FullWidthRow)`
    margin-bottom: 10px;
`;

type FilesProps = {
    validators;
    history;
} & WrappedComponentProps;

const formatMessages = ({ intl }) => ({
    lastWeek: intl.formatMessage({ id: 'search.fields.searchWindow.lastWeek' }),
    lastMonth: intl.formatMessage({ id: 'search.fields.searchWindow.lastMonth' }),
    lastThreeMonths: intl.formatMessage({ id: 'search.fields.searchWindow.lastThreeMonths' }),
    lastYear: intl.formatMessage({ id: 'search.fields.searchWindow.lastYear' }),
    all: intl.formatMessage({ id: 'search.fields.searchWindow.all' }),
    globalResult: intl.formatMessage({ id: 'global.results' }).toLowerCase(),
    title: intl.formatMessage({ id: 'menuLeft.navigation.search' }),
    documentTitle: intl.formatMessage({ id: 'title.search' }),
    placeholderMessage: intl.formatMessage({ id: 'search.feeds' }),
    placeholderFiles: intl.formatMessage({ id: 'search.files' }),
    feedTab: intl.formatMessage({ id: 'search.tabs.discussions' }),
    fileTab: intl.formatMessage({ id: 'menuLeft.navigation.files' })
});

const prepareDate = (isFromDate: boolean, delta: string) => {
    let d = new Date();
    if (isFromDate) {
        d.setHours(1);
        d.setMinutes(0);
        d.setSeconds(0);
        d.setMilliseconds(0);
        d.setDate(d.getDate() - parseInt(delta, 10));
    } else {
        d.setHours(24);
        d.setMinutes(59);
        d.setSeconds(59);
        d.setMilliseconds(999);
    }
    return d.toISOString();
};

const Files = ({ intl, validators, history }: FilesProps) => {
    const screens = useBreakpoint();
    const [pageSizeOptions, setPageSizeOptions] = useState<number[]>(getPageSizeOptions(screens));
    const [searchValue, setSearchValue] = useState<string>('');
    const [searchWindowValue, setSearchWindowValue] = useState<string>('0');
    const [page, setPage] = useState<number>(1);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>('');
    const [limit, setLimit] = useState<number>(pageSizeOptions[0]);
    const [feedItemType, setFeedItemType] = useState<string>('content');
    const [activeTab, setActiveTab] = useState('searchFeeds');
    const i18n = useMemo(() => formatMessages({ intl }), [intl]);
    const warningMessage = useMemo(
        () => validators.searchLength(debouncedSearchValue),
        [debouncedSearchValue, validators]
    );
    const { isLoading, data: results = {} } = useSearch({
        query: warningMessage ? '' : debouncedSearchValue,
        ...(searchWindowValue === '-1'
            ? {}
            : {
                  fromDate: prepareDate(true, searchWindowValue),
                  toDate: prepareDate(false, searchWindowValue)
              }),
        filters: ['feedItems'],
        subFilters: { feedItemType },
        page,
        limit,
        highlight: true
    });

    const onSearchWindowChange = (value: string) => {
        setSearchWindowValue(value);
        setPage(1);
    };

    const onSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
        setPage(1);
    };

    useEffect(() => {
        const params = new URLSearchParams(history.location.search);

        if (searchValue) {
            params.set('q', encodeURIComponent(searchValue));
            documentTitleManager.setTitle(
                intl.formatMessage({ id: 'title.search.term' }, { term: searchValue })
            );
        } else {
            documentTitleManager.setTitle(i18n.documentTitle);
        }
        if (searchWindowValue !== '0') {
            params.set('searchWindow', encodeURIComponent(searchWindowValue));
        } else {
            setSearchWindowValue('30');
        }

        history.push({
            pathname: history.location.pathname,
            search: `?${params.toString()}`
        });
    }, [history, searchValue, searchWindowValue]);

    const onShowSizeChange = (_, pageSize) => setLimit(pageSize);

    useDebounce(() => setDebouncedSearchValue(searchValue), 200, [searchValue]);

    useEffect(() => {
        const newOptions = getPageSizeOptions(screens);
        setPageSizeOptions(newOptions);
        setLimit(newOptions[0]);
    }, [screens]);

    useEffect(() => {
        documentTitleManager.setTitle(i18n.documentTitle);
    }, []);

    const handleTabChange = useCallback(
        activeKey => {
            setActiveTab(activeKey);
            history.push(getUrl(activeKey));
        },
        [setActiveTab, history]
    );

    useEffect(() => {
        const { pathname, search } = history.location;
        if (pathname?.indexOf(getUrl('searchFiles')) !== -1) {
            setFeedItemType('file');
            setActiveTab('searchFiles');
        } else {
            setFeedItemType('content');
            setActiveTab('searchFeeds');
        }

        if (search) {
            const params = new URLSearchParams(search);
            const searchParam = params.get('q');
            const searchWindowParam = params.get('searchWindow');

            setSearchValue(decodeURIComponent(searchParam || ''));
            setSearchWindowValue(decodeURIComponent(searchWindowParam || '30'));
        }
    }, [history.location.pathname]);

    const items = useMemo(
        () => [
            {
                key: 'searchFeeds',
                label: (
                    <StyledLabel>
                        <Icon size="xl" iconName="MessageCircle" />
                        {i18n.feedTab}
                    </StyledLabel>
                ),
                children: (
                    <>
                        {!isLoading && results?.feedItems && <MessageList {...results} />}
                        {!!results?.feedItems?.results?.length && (
                            <FullWidthRow align="middle" justify="center" wrap={false}>
                                <Pagination
                                    current={page}
                                    onChange={setPage}
                                    total={results?.feedItems?.paging.total || 1}
                                    onShowSizeChange={onShowSizeChange}
                                    pageSizeOptions={pageSizeOptions}
                                    pageSize={limit}
                                />
                            </FullWidthRow>
                        )}
                    </>
                )
            },
            {
                key: 'searchFiles',
                label: (
                    <StyledLabel>
                        <Icon size="xl" iconName="Paperclip" />
                        {i18n.fileTab}
                    </StyledLabel>
                ),
                children: (
                    <>
                        {!isLoading && results?.feedItems && <FileList {...results} />}
                        {!!results?.feedItems?.results?.length && (
                            <FullWidthRow align="middle" justify="center" wrap={false}>
                                <Pagination
                                    current={page}
                                    onChange={setPage}
                                    total={results?.feedItems?.paging.total || 1}
                                    onShowSizeChange={onShowSizeChange}
                                    pageSizeOptions={pageSizeOptions}
                                    pageSize={limit}
                                />
                            </FullWidthRow>
                        )}
                    </>
                )
            }
        ],
        [intl, isLoading, results, page, limit, pageSizeOptions]
    );

    return (
        <StyledPageLayout className="ui-contact">
            <CategoryTitle
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                label={i18n.title}
                icon={<Icon size="xl" iconName="Search" />}
            />
            <FullWidthRowWithMargin>
                <Col flex="auto">
                    <StyledInput
                        placeholder={
                            activeTab === 'searchFeeds'
                                ? i18n.placeholderMessage
                                : i18n.placeholderFiles
                        }
                        prefix={<SearchOutlined />}
                        onChange={onSearchInputChange}
                        value={searchValue}
                    />
                    {warningMessage && <StyledText type="danger">{warningMessage}</StyledText>}
                </Col>
            </FullWidthRowWithMargin>
            <FullWidthRowWithMargin>
                <Select
                    placeholder="Search window"
                    options={[
                        { value: '7', label: i18n.lastWeek },
                        { value: '30', label: i18n.lastMonth },
                        { value: '90', label: i18n.lastThreeMonths },
                        { value: '365', label: i18n.lastYear },
                        { value: '-1', label: i18n.all }
                    ]}
                    value={searchWindowValue.toString()}
                    onChange={onSearchWindowChange}
                    style={{ width: 300 }}
                ></Select>
            </FullWidthRowWithMargin>
            <FullWidthRowWithMargin>
                <Text strong type="secondary">
                    {results?.feedItems?.paging?.total || 0} {i18n.globalResult}
                </Text>
            </FullWidthRowWithMargin>
            <StyledTabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={items}
                destroyInactiveTabPane
            />
            {isLoading && <LoadingOutlined />}
        </StyledPageLayout>
    );
};

export default injectValidators(injectIntl(Files));
